import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  SunMedium,
  Wind,
  Cpu,
  TrendingUp,
  Activity,
  Shield,
  Clock,
  Landmark,
  GraduationCap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CaseStudies = () => {
  const navigate = useNavigate();

  const caseStudies = [
    {
      id: "solar",
      title: "Proactive solar plant maintenance",
      location: "Bengaluru",
      category: "Solar",
      badgeClass: "bg-secondary/10 text-secondary border-secondary/20",
      icon: SunMedium,
      problem:
        "A solar operator had no remote visibility into daily generation performance. Faults were discovered only after output dropped — forcing reactive dispatches to remote sites, increasing downtime, operational costs, and revenue loss.",
      solution:
        "Built a custom remote monitoring platform using open-source OPC UA tooling on top of the client’s existing infrastructure. The system continuously collects generation data, detects anomalies in real time, and surfaces predictive maintenance alerts before faults escalate.",
      results: [
        {
          label: "Advance warning",
          value: "72h+",
          icon: Clock,
        },
        {
          label: "Fewer site visits",
          value: "~40%",
          icon: TrendingUp,
        },
        {
          label: "Remote visibility",
          value: "24/7",
          icon: Activity,
        },
        {
          label: "No vendor lock-in",
          value: "Open",
          icon: Shield,
        },
      ],
      technologies: [
        "OPC UA",
        "Predictive Maintenance",
        "Remote Monitoring",
        "Open-source",
      ],
      solutionRoute: "/solutions/solar",
    },

    {
      id: "wind",
      title: "Legacy OPC UA replacement",
      location: "Wind Power Plant",
      category: "Wind",
      badgeClass: "bg-primary/10 text-primary border-primary/20",
      icon: Wind,
      problem:
        "A wind power operator relied on a 20-year-old proprietary OPC UA client that could not process modern data volumes and required manual restart after every network disruption. Adding new turbines or servers was slow, vendor-dependent, and expensive.",
      solution:
        "Replaced the legacy stack with a modern open-source OPC UA system capable of processing 30,000+ nodes every 2 seconds. Added self-healing recovery and config-based onboarding for new assets without code changes.",
      results: [
        {
          label: "Nodes processed",
          value: "30K+",
          icon: Activity,
        },
        {
          label: "Cycle interval",
          value: "2s",
          icon: Clock,
        },
        {
          label: "Downtime at migration",
          value: "Zero",
          icon: Shield,
        },
        {
          label: "Integration speed",
          value: "Weeks → Hours",
          icon: TrendingUp,
        },
      ],
      technologies: ["OPC UA", "Kafka", "Industrial IoT", "Open-source"],
      solutionRoute: "/solutions/wind",
    },

    {
      id: "motor",
      title: "Multi-vendor motor synchronisation",
      location: "Industrial Facility",
      category: "Industrial Automation",
      badgeClass: "bg-secondary/10 text-secondary border-secondary/20",
      icon: Cpu,
      problem:
        "A manufacturing facility operated motors from multiple OEMs, each requiring separate proprietary software with no interoperability. Cross-vendor coordination was manual, slow, and operationally fragmented.",
      solution:
        "Implemented a unified OPC UA control layer across all OEM devices, combined with Time-Sensitive Networking (TSN) for deterministic synchronisation. Delivered a fully open architecture without proprietary software lock-in.",
      results: [
        {
          label: "OEM support",
          value: "All vendors",
          icon: Shield,
        },
        {
          label: "Control interface",
          value: "Single",
          icon: Activity,
        },
        {
          label: "Licensing",
          value: "Zero proprietary",
          icon: TrendingUp,
        },
        {
          label: "Automation",
          value: "Fully programmable",
          icon: Clock,
        },
      ],
      technologies: ["OPC UA", "TSN", "Motor Control", "Open-source"],
      solutionRoute: "/solutions/motor",
    },
    {
      id: "bfsi",
      title: "Monolith to microservices banking modernization",
      location: "BFSI / FinTech",
      category: "Banking Modernization",
      badgeClass: "bg-primary/10 text-primary border-primary/20",
      icon: Landmark,
      problem:
        "A decade-old monolithic banking platform made every deployment risky. A single service failure could impact unrelated operations, scaling required scaling everything, audit trails were fragmented, and disaster recovery was slow and manual.",
      solution:
        "Re-architected the platform into Kafka-driven microservices with Kafka + Kraft, ElasticSearch hot/warm/cold tiering, MirrorMaker 2 DC/DR failover, and decoupled operational connectors — delivering zero-disruption modernization for live financial systems.",
      results: [
        {
          label: "Independent services",
          value: "5+",
          icon: Activity,
        },
        {
          label: "Downtime during migration",
          value: "~0",
          icon: Shield,
        },
        {
          label: "Storage model",
          value: "3-tier",
          icon: TrendingUp,
        },
        {
          label: "Resilience",
          value: "DC/DR",
          icon: Clock,
        },
      ],
      technologies: [
        "Kafka",
        "Kraft",
        "ElasticSearch",
        "MirrorMaker 2",
        "Microservices",
        "Open-source",
      ],
      solutionRoute: "/solutions/bfsi",
    },

    {
      id: "edtech",
      title: "Child-safe talent ecosystem platform",
      location: "EdTech / Mobile App",
      category: "EdTech",
      badgeClass: "bg-secondary/10 text-secondary border-secondary/20",
      icon: GraduationCap,
      problem:
        "Children lacked a safe, privacy-first digital platform to showcase talent without exposure to unsafe public social networks. Parents had limited oversight, competitions were unstructured, and there was no integrated developmental ecosystem.",
      solution:
        "Built a Flutter-based privacy-first mobile ecosystem for under-18 users featuring parental consent, anonymous profiles, talent feeds, structured competitions, and Mind Garden cognitive development — all with Android + iOS deployment from a single codebase.",
      results: [
        {
          label: "Target audience",
          value: "U18 only",
          icon: Shield,
        },
        {
          label: "Public PII exposure",
          value: "Zero",
          icon: Activity,
        },
        {
          label: "Platforms",
          value: "Android + iOS",
          icon: TrendingUp,
        },
        {
          label: "Core modules",
          value: "5",
          icon: Clock,
        },
      ],
      technologies: [
        "Flutter",
        "Python",
        "Privacy-first",
        "Mobile App",
        "Competition Engine",
        "Mind Garden",
      ],
      solutionRoute: "/solutions/edtech",
    },
  ];

  return (
    <section
      id="case-studies"
      className="py-16 md:py-24 bg-gradient-to-b from-background via-secondary/5 to-muted/20"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 px-4 py-1.5 shadow-sm">
            Real Field Deployments
          </Badge>

          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight bg-duo-gradient bg-clip-text text-transparent">
            Problems we’ve solved in the field
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Three real enterprise deployments across renewable energy,
            industrial telemetry, and automation. Anonymous where needed.
            Metrics marked as estimates are pending client sign-off.
          </p>
        </div>

        {/* Timeline-style layout */}
        <div className="space-y-10">
          {caseStudies.map((study, index) => {
            const MainIcon = study.icon;

            return (
              <Card
                key={study.id}
                className="overflow-hidden border border-border/60 shadow-sm hover:shadow-xl transition-all duration-300 rounded-3xl"
              >
                <CardContent className="p-0">
                  {/* Top */}
                  <div className="p-8 border-b bg-card">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                      <div className="flex gap-5">
                        <div
                          className={`h-14 w-14 rounded-2xl ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} flex items-center justify-center flex-shrink-0 shadow-sm border ${index % 2 === 0 ? "border-primary/20" : "border-secondary/20"}`}
                        >
                          <MainIcon
                            className={`h-7 w-7 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                          />
                        </div>

                        <div>
                          <Badge
                            variant="outline"
                            className={`mb-3 ${study.badgeClass}`}
                          >
                            {study.category} · {study.location}
                          </Badge>

                          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                            {study.title}
                          </h3>

                          <p className="text-sm text-muted-foreground">
                            Case Study #{String(index + 1).padStart(2, "0")}
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={() => navigate(study.solutionRoute)}
                        className="rounded-xl"
                      >
                        View Full Solution
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Main Body */}
                  <div className="grid lg:grid-cols-3 gap-0">
                    {/* Problem */}
                    <div className="p-8 border-b lg:border-b-0 lg:border-r bg-primary/5">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                        Problem
                      </p>

                      <p className="text-muted-foreground leading-relaxed">
                        {study.problem}
                      </p>
                    </div>

                    {/* Solution */}
                    <div className="p-8 border-b lg:border-b-0 lg:border-r bg-secondary/5">
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-4">
                        Solution
                      </p>

                      <p className="text-muted-foreground leading-relaxed">
                        {study.solution}
                      </p>
                    </div>

                    {/* Results */}
                    <div className="p-8">
                      <p
                        className={`text-xs uppercase tracking-[0.2em] font-bold ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} mb-4`}
                      >
                        Results
                      </p>

                      <div className="space-y-4">
                        {study.results.map((result, idx) => {
                          const ResultIcon = result.icon;

                          return (
                            <div
                              key={idx}
                              className="flex items-center gap-4 rounded-xl border border-border/50 p-3"
                            >
                              <div
                                className={`h-10 w-10 rounded-lg ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} flex items-center justify-center shadow-sm`}
                              >
                                <ResultIcon
                                  className={`h-5 w-5 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"} `}
                                />
                              </div>

                              <div>
                                <div className="font-bold text-foreground">
                                  {result.value}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {result.label}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-8 py-6 border-t bg-muted/20">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex flex-wrap gap-2">
                        {study.technologies.map((tech, techIndex) => (
                          <Badge
                            key={techIndex}
                            className={`rounded-md ${index % 2 === 0 ? "bg-primary/10 text-primary border-primary/20" : "bg-secondary/10 text-secondary border-secondary/20"}`}
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>

                      <Button
                        variant="ghost"
                        onClick={() => navigate(study.solutionRoute)}
                        className="justify-start md:justify-center"
                      >
                        Explore architecture & implementation
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
